import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Icon,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { formatFileSize } from "../../common/documents/attachmentDetails";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiBriefcase, FiDollarSign, FiEdit, FiFile, FiFileText, FiUser } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { NeedRecurrence, type NeedDto, type NeedPatchDto } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { organizationHasSeveralDepartments } from "../../common/departments/departmentVisibility";
import { useEditor } from "../../common/editing/save-coordinator";
import { DownloadDocumentIconButton } from "../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { TipTap, isTiptapContentEmpty } from "../../common/input/TipTap/TipTap";
import { LabelValueGrid } from "../sourcing-events/portal/view-single/LabelValueGrid";
import { SectionCard } from "../sourcing-events/portal/view-single/SectionCard";
import { urls } from "../../urls";
import { NeedActions } from "./NeedActions";
import { NeedAttachmentUpload } from "./NeedAttachmentUpload";
import { StatusDotLabel } from "../../common/components/StatusDotLabel";
import { usePlannedStartLabel } from "../../common/needs/plannedStart";
import { HELD_BY_PROCUREMENT } from "../../common/needs/useNeedStatusLabel";
import { ChangeNeedResponsibleButton } from "./ChangeNeedResponsibleButton";
import { NeedDecisionComment } from "./NeedDecisionComment";
import { NeedStatusTag } from "./NeedStatusTag";
import { NeedProcurerAssessment } from "./NeedProcurerAssessment";
import { NeedStatusStepper } from "./NeedStatusStepper";
import { useNeedPriorityLabel } from "./useNeedPriorityLabel";
import { useNeedValueLabel, useNeedValueText } from "./useNeedValueLabel";
import { useRunsPublicProcurements } from "../../common/organization/useRunsPublicProcurements";
import { useCpvDivisions } from "../../common/cpv/useCpvDivisions";

const DASH = "–";

interface Props {
  needId: string;
  refetch?: () => void;
}

export const NeedViewBody = ({ needId, refetch }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useEditor<NeedDto, NeedPatchDto>(needId);
  const { nameOf: categoryName } = useCpvDivisions();
  const priorityLabel = useNeedPriorityLabel();
  const plannedStartLabel = usePlannedStartLabel();
  const valueLabel = useNeedValueLabel();
  const valueText = useNeedValueText();
  const recurrenceLabel = (recurrence: NeedRecurrence) =>
    recurrence === NeedRecurrence.Ongoing ? t("Ongoing need") : t("One-off need");
  const runsPublicProcurements = useRunsPublicProcurements();
  const { selectedOrg } = useLoggedInWithOrgContextState();

  return (
    <Box maxW="container.lg" mx="auto" pb="10">
      <Button
        size="sm"
        variant="ghost"
        leftIcon={<Icon as={FiArrowLeft} />}
        color="smMuted"
        onClick={() => navigate(urls.app.needs.mine)}
        mb="3"
      >
        {t("Back to my needs")}
      </Button>

      <Flex justify="space-between" align="flex-start" gap="4" mb="6" wrap="wrap">
        <Box>
          <Heading as="h1" size="lg" color="smText" mb="2">
            {state.title?.trim() || t("Untitled")}
          </Heading>
          <HStack spacing="3" color="smMuted" fontSize="sm">
            <NeedStatusTag status={state.status} />
            <Text>
              {t("Reported by")} {personName(state.createdBy)} · {formatDate(state.createdAt)}
            </Text>
          </HStack>
        </Box>
        {state.canEdit && (
          <Button
            colorScheme="teal"
            variant="outline"
            leftIcon={<Icon as={FiEdit} />}
            onClick={() => navigate(urls.app.needs.edit.go(state.id))}
          >
            {t("Edit")}
          </Button>
        )}
      </Flex>

      <Stack spacing="5">
        <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" p="6">
          <NeedStatusStepper status={state.status} />
        </Box>

        <NeedDecisionComment need={state} />

        <NeedActions needId={needId} />

        <SectionCard icon={FiFileText} title={t("Description")}>
          {!isTiptapContentEmpty(state.description) ? (
            <TipTap content={state.description ?? ""} editable={false} hasBorder={false} padding="0" />
          ) : (
            <Text color="smMuted" fontSize="sm">
              {DASH}
            </Text>
          )}
        </SectionCard>

        <SectionCard icon={FiDollarSign} title={t("Value")}>
          <Stack spacing="5">
            <LabelValueGrid
              rows={[
                { label: valueLabel, value: valueText(state.estimatedValue) },
                ...(runsPublicProcurements
                  ? [
                      {
                        label: t("One-off or ongoing need?"),
                        value: state.recurrence ? recurrenceLabel(state.recurrence) : DASH,
                      },
                      ...(state.recurrence === NeedRecurrence.Ongoing
                        ? [{ label: t("Period"), value: state.periodMonths ? t("{{count}} months", { count: state.periodMonths }) : DASH }]
                        : []),
                      { label: t("Quantity or scope"), value: state.quantity || DASH },
                      { label: t("Budget line"), value: state.budgetLine || DASH },
                    ]
                  : []),
              ]}
            />
            {runsPublicProcurements && !isTiptapContentEmpty(state.valueAssessment) && (
              <Stack spacing="1">
                <Text color="smMuted" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                  {t("Initial value assessment")}
                </Text>
                <TipTap content={state.valueAssessment ?? ""} editable={false} hasBorder={false} padding="0" />
              </Stack>
            )}
          </Stack>
        </SectionCard>

        <NeedProcurerAssessment needId={needId} />

        <SectionCard icon={FiBriefcase} title={t("Procurement")}>
          <LabelValueGrid
            rows={[
              ...(organizationHasSeveralDepartments(selectedOrg)
                ? [{ label: t("Department"), value: state.department?.name ?? DASH }]
                : []),
              { label: t("Priority"), value: state.priority ? priorityLabel(state.priority) : DASH },
              ...(runsPublicProcurements
                ? [
                    {
                      label: t("What does the procurement concern?"),
                      value: state.procurementType ? t(state.procurementType) : DASH,
                    },
                  ]
                : []),
              { label: t("Category"), value: categoryName(state.category) ?? DASH },
              ...(runsPublicProcurements ? [{ label: t("Delivery place"), value: state.deliveryPlace || DASH }] : []),
              { label: t("Desired delivery date"), value: formatLocalDate(state.deliveryDate) },
              ...(state.plannedStart
                ? [
                    {
                      label: t("Planned start"),
                      value: state.plannedStartHasPassed ? (
                        <HStack spacing="2" wrap="wrap">
                          <Text>{plannedStartLabel(state.plannedStart)}</Text>
                          <StatusDotLabel variant="issue" label={t("Passed")} />
                        </HStack>
                      ) : (
                        plannedStartLabel(state.plannedStart)
                      ),
                    },
                  ]
                : []),
              ...(state.responsibleBuyer || HELD_BY_PROCUREMENT.includes(state.status)
                ? [
                    {
                      label: t("Responsible buyer"),
                      value: (
                        <HStack spacing="3">
                          <Text>{state.responsibleBuyer ? personName(state.responsibleBuyer) : t("No one responsible")}</Text>
                          {state.canChangeResponsible && (
                            <ChangeNeedResponsibleButton need={state} ownerOrgId={selectedOrg.id} />
                          )}
                        </HStack>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </SectionCard>

        <SectionCard icon={FiUser} title={t("Contact")}>
          <LabelValueGrid
            rows={[
              { label: t("Contact person"), value: state.contactPerson ? personName(state.contactPerson) : DASH },
              { label: t("Reported by"), value: personName(state.createdBy) },
              { label: t("Created"), value: formatDate(state.createdAt) },
            ]}
          />
        </SectionCard>

        <SectionCard icon={FiFile} title={t("Attachments")} count={state.documents?.length}>
          {state.canAttach && refetch && <NeedAttachmentUpload needId={state.id} documents={state.documents ?? NO_DOCUMENTS} refetch={refetch} />}
          {(state.documents?.length ?? 0) === 0 ? (
            <Text color="smMuted" fontSize="sm">
              {t("No attachments")}
            </Text>
          ) : (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>{t("Name")}</Th>
                    <Th isNumeric>{t("Size")}</Th>
                    <Th>{t("Uploaded by")}</Th>
                    <Th>{t("Uploaded at")}</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {(state.documents ?? []).map((doc) => (
                    <Tr key={doc.id}>
                      <Td color="smText">{doc.name}</Td>
                      <Td color="smMuted" isNumeric>
                        {formatFileSize(doc.size)}
                      </Td>
                      <Td color="smMuted">{personName(doc.uploadedBy)}</Td>
                      <Td color="smMuted">{formatDate(doc.uploadedAt)}</Td>
                      <Td isNumeric>
                        <DownloadDocumentIconButton documentId={doc.id} size="sm" />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>
      </Stack>
    </Box>
  );
};

const personName = (p: { firstName?: string | null; lastName?: string | null }): string => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name || DASH;
};


const formatDate = (instant: string): string => {
  const d = new Date(instant);
  return Number.isNaN(d.getTime())
    ? instant
    : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const formatLocalDate = (yyyyMmDd: string | null | undefined): string => {
  if (!yyyyMmDd) return DASH;
  const parts = yyyyMmDd.split("-");
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : yyyyMmDd;
};
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);


const NO_DOCUMENTS: NonNullable<NeedDto["documents"]> = [];
